import{useCallback,useMemo,useRef,useState}from"react";
import{securityApi}from"../../lib/api";
import{useResource}from"../../lib/useResource";
import{useAppI18n}from"../../lib/appI18n";
import{EmptyState,InlineNotice,LoadingLine}from"../../components/State";
import{ResourceList,ResourceTable,SectionHeader,StateText,WorkspaceHeader}from"../../components/Foundation";
import{runLoginCeremony}from"../../lib/webauthn";
import{deviceAuthorityApi}from"../../lib/api";
import{unlockSecurityAuthority}from"../../lib/securityAuthority";
import{
  useStatusLabel,
  useHumanDuration,
  useEventLabel,
  usePasskeyType,
  useAuthMethodLabel,
  useClientClassLabel,
  useRelativeTime,
}from"./securityTerminology";
import{statusTone}from"./passkeyDetailModel";
import{
  clientDescriptor as personClientDescriptor,
  relativeMinutes as personMinutes,
  sessionState as personSessionState,
  stateTone as personStateTone,
}from"./sessionAttributionModel";
import{
  PERSON_TABS,
  classifyPersonDetailError,
  countSecurityPosture,
  isValidSubjectId,
  personDisplayName,
  personEmail,
  personEmailSource,
  personEvents,
  personPackages,
  personRoles,
  recoveryTone,
  sessionKey,
  severityTone,
  type PersonTab,
}from"./personDetailModel";

type Translator=(key:string)=>string;

function Protected({
  error,
  t,
  onUnlocked,
}:{
  error:string|null;
  t:Translator;
  onUnlocked:()=>unknown;
}){
  const[busy,setBusy]=useState(false);
  const[actionError,setActionError]=useState<string|null>(null);

  if(!error)return null;

  const sessionRequired=
    /passkey_session_required|authentication_required/i.test(error);

  const signIn=async()=>{
    setBusy(true);
    setActionError(null);
    try{
      const options=await deviceAuthorityApi.loginOptions();
      const proof=await runLoginCeremony(options);
      await deviceAuthorityApi.loginVerify(proof);
      await onUnlocked();
    }catch(e){
      setActionError(e instanceof Error?e.message:t("sc.signInFailed"));
    }finally{
      setBusy(false);
    }
  };

  const unlock=async()=>{
    setBusy(true);
    setActionError(null);
    try{
      await unlockSecurityAuthority();
      await onUnlocked();
    }catch(e){
      setActionError(e instanceof Error?e.message:t("sc.authorityUnlockFailed"));
    }finally{
      setBusy(false);
    }
  };

  return <InlineNotice
    tone="attention"
    title={
      sessionRequired
        ?t("sc.passkeySessionRequiredTitle")
        :t("sc.authorityRequiredTitle")
    }
    detail={
      actionError
        ?actionError
        :sessionRequired
          ?t("sc.passkeySessionRequiredDetail")
          :t("sc.authorityRequiredDetail")
    }
    action={
      <button
        type="button"
        className="button secondary"
        disabled={busy}
        onClick={()=>
          void(
            sessionRequired
              ?signIn()
              :unlock()
          )
        }
      >
        {busy
          ?t("sc.waitingForPasskey")
          :sessionRequired
            ?t("sc.signInWithPasskey")
            :t("sc.unlockWithPasskey")}
      </button>
    }
  />;
}

function formatDate(value:string|Date|null|undefined){
  if(!value)return"—";
  const date=value instanceof Date?value:new Date(value);
  if(Number.isNaN(date.getTime()))return String(value);
  return new Intl.DateTimeFormat(undefined,{dateStyle:"medium",timeStyle:"short"}).format(date);
}

export function PersonDetail({
  subjectId,
  onBack,
  onOpenPasskey,
}:{
  subjectId:string;
  onBack:()=>void;
  onOpenPasskey?:(credentialUuid:string)=>void;
}){
  const i18n=useAppI18n();
  const t=useCallback((k:string):string=>i18n.t(k),[i18n]);
  const statusLabel=useStatusLabel();
  const humanDuration=useHumanDuration();
  const eventLabel=useEventLabel();
  const passkeyType=usePasskeyType();
  const authLabel=useAuthMethodLabel();
  const clientLabel=useClientClassLabel();
  const relativeTime=useRelativeTime();
  const sessionNow=Date.now();

  const[tab,setTab]=useState<PersonTab>("general");
  const tabRefs=useRef<Partial<Record<PersonTab,HTMLButtonElement|null>>>({});

  const subjectIdValid=isValidSubjectId(subjectId);

  // A malformed subject reference is a client-side navigation mistake.
  // No request is issued; the read model is never asked to resolve it.
  const loadPerson=useCallback(async()=>{
    if(!isValidSubjectId(subjectId))throw new Error("person_reference_invalid");
    return securityApi.personDetail(subjectId);
  },[subjectId]);

  const loadActivity=useCallback(async()=>{
    if(!isValidSubjectId(subjectId))throw new Error("person_reference_invalid");
    return securityApi.personActivity(subjectId,50);
  },[subjectId]);

  const personResource=useResource(loadPerson);
  const activityResource=useResource(loadActivity);

  const refresh=useCallback(()=>{
    void personResource.refresh();
    void activityResource.refresh();
  },[personResource,activityResource]);

  const data=personResource.data;
  const profile=data?.profile;
  const person=data?.person;
  const addresses=useMemo(()=>data?.addresses??[],[data]);
  const passkeys=useMemo(()=>data?.passkeys??[],[data]);
  const sessions=useMemo(()=>data?.sessions??[],[data]);
  const recovery=data?.recovery;
  const events=useMemo(()=>personEvents(activityResource.data),[activityResource.data]);

  const error=personResource.status==="error"&&!personResource.data
    ?personResource.error
    :null;
  const failure=classifyPersonDetailError(error);

  const counts=useMemo(
    ()=>countSecurityPosture(passkeys,sessions),
    [passkeys,sessions],
  );
  const activePasskeys=useMemo(
    ()=>passkeys.filter(p=>p.status==="ACTIVE"),
    [passkeys],
  );
  const displayName=personDisplayName(data);
  const email=personEmail(data);
  const emailSource=personEmailSource(data);
  const locale=profile?.locale||"";
  const timezone=profile?.timezone||"";
  const roles=useMemo(()=>personRoles(data),[data]);
  const packages=useMemo(()=>personPackages(data),[data]);

  const moveTab=(delta:number)=>{
    const index=PERSON_TABS.indexOf(tab);
    const next=PERSON_TABS[(index+delta+PERSON_TABS.length)%PERSON_TABS.length];
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  const backButton=(
    <button
      type="button"
      className="person-back"
      onClick={onBack}
    >
      <span aria-hidden="true">&#8592;</span>
      <span>{t("sc.backToPeople")}</span>
    </button>
  );

  if(!subjectIdValid){
    return <div className="page person-detail">
      {backButton}
      <EmptyState
        title={t("sc.invalidSubjectId")}
        detail={t("sc.invalidSubjectIdDesc")}
        action={backButton}
      />
    </div>;
  }

  return <div className="page person-detail">
    <WorkspaceHeader
      eyebrow={t("sc.personDetail")}
      title={displayName||t("sc.unknownPerson")}
      description={email||t("sc.noEmailRecorded")}
      actions={
        <>
          {backButton}
          {personResource.status==="loading"
            ?<LoadingLine label={t("sc.personSections")}/>
            :null}
        </>
      }
    />

    {failure==="not-found"&&
      <EmptyState
        title={t("sc.personNotFound")}
        detail={t("sc.personNotFoundDesc")}
        action={backButton}
      />}

    {failure!=="not-found"&&error&&
      <EmptyState
        title={t("sc.personLoadFailed")}
        detail={t("sc.personLoadFailedDesc")}
        action={
          <button
            type="button"
            className="button secondary"
            onClick={refresh}
          >
            {t("sc.retry")}
          </button>
        }
      />}

    <Protected error={failure==="guarded"?error:null} t={t} onUnlocked={refresh}/>

    {personResource.status==="loading"&&!data
      ?<LoadingLine label={t("sc.personDetail")}/>
      :null}

    {data&&failure!=="not-found"&&<>
      <div
        className="person-tabs"
        role="tablist"
        aria-label={t("sc.personSections")}
        onKeyDown={e=>{
          if(e.key==="ArrowRight"){e.preventDefault();moveTab(1);}
          if(e.key==="ArrowLeft"){e.preventDefault();moveTab(-1);}
        }}
      >
        {PERSON_TABS.map(x=>
          <button
            key={x}
            type="button"
            role="tab"
            id={`person-tab-${x}`}
            aria-selected={tab===x}
            aria-controls={`person-panel-${x}`}
            tabIndex={tab===x?0:-1}
            ref={el=>{tabRefs.current[x]=el;}}
            className={tab===x?"selected":""}
            onClick={()=>setTab(x)}
          >
            {t(`sc.${x}`)}
          </button>
        )}
      </div>

      <div
        className="person-section"
        role="tabpanel"
        id={`person-panel-${tab}`}
        aria-labelledby={`person-tab-${tab}`}
      >
        {tab==="general"&&
          <div className="person-section-body">
            <SectionHeader title={t("sc.general")} description={t("sc.generalDesc")}/>
            <div className="person-grid">
              <section className="person-card">
                <h3>{t("sc.identity")}</h3>
                <dl className="person-facts">
                  <div><dt>{t("sc.displayName")}</dt><dd>{displayName||"—"}</dd></div>
                  <div><dt>{t("sc.preferredName")}</dt><dd>{profile?.preferred_name||t("sc.notSet")}</dd></div>
                  <div><dt>{t("sc.subjectId")}</dt><dd><code>{person?.subject_id}</code></dd></div>
                  <div><dt>{t("sc.status")}</dt><dd><StateText tone={profile?.subject_status==="ACTIVE"?"positive":"neutral"}>{statusLabel(profile?.subject_status||"UNKNOWN")}</StateText></dd></div>
                  <div><dt>{t("sc.authUserId")}</dt><dd>{profile?.auth_user_id?<code>{profile.auth_user_id}</code>:t("sc.unlinked")}</dd></div>
                  <div><dt>{t("sc.authStatus")}</dt><dd><StateText tone={profile?.auth_status==="ACTIVE"?"positive":"neutral"}>{statusLabel(profile?.auth_status||"UNKNOWN")}</StateText></dd></div>
                  <div><dt>{t("sc.membership")}</dt><dd><StateText tone="neutral">{statusLabel(profile?.membership_status||"UNKNOWN")}</StateText></dd></div>
                  <div><dt>{t("sc.organizationRole")}</dt><dd>{profile?.organization_role||t("sc.none")}</dd></div>
                  <div><dt>{t("sc.joinedAt")}</dt><dd>{formatDate(profile?.joined_at)}</dd></div>
                  <div><dt>{t("sc.locale")}</dt><dd>{locale||t("sc.notSet")}</dd></div>
                  <div><dt>{t("sc.timezone")}</dt><dd>{timezone||t("sc.notSet")}</dd></div>
                </dl>
              </section>

              <section className="person-card">
                <h3>{t("sc.securitySummary")}</h3>
                <dl className="person-facts">
                  <div><dt>{t("sc.activePasskeys")}</dt><dd><strong>{counts.activePasskeys}</strong></dd></div>
                  <div><dt>{t("sc.unlockPasskeys")}</dt><dd><strong>{counts.unlockPasskeys}</strong></dd></div>
                  <div><dt>{t("sc.trustGroups")}</dt><dd><strong>{counts.trustGroups}</strong></dd></div>
                  <div><dt>{t("sc.activeSessions")}</dt><dd><strong>{counts.activeSessions}</strong></dd></div>
                  <div><dt>{t("sc.recoveryState")}</dt><dd><StateText tone={recoveryTone(recovery?.recovery_state)}>{recovery?statusLabel(recovery.recovery_state):t("sc.unknown")}</StateText></dd></div>
                  <div><dt>{t("sc.offlineRecovery")}</dt><dd><StateText tone={recovery?.offline_recovery_ready?"positive":"attention"}>{recovery?.offline_recovery_ready?t("sc.ready"):t("sc.notReady")}</StateText></dd></div>
                </dl>
              </section>
            </div>
          </div>
        }

        {tab==="contact"&&
          <div className="person-section-body">
            <SectionHeader title={t("sc.contact")} description={t("sc.contactDesc")}/>
            <div className="person-grid">
              <section className="person-card">
                <h3>{t("sc.email")}</h3>
                <dl className="person-facts">
                  <div><dt>{t("sc.primaryEmail")}</dt><dd>{email||t("sc.notRecorded")}</dd></div>
                  <div><dt>{t("sc.emailVerified")}</dt><dd><StateText tone={profile?.auth_email_verified?"positive":"attention"}>{profile?.auth_email_verified?t("sc.verified"):t("sc.pending")}</StateText></dd></div>
                  <div><dt>{t("sc.emailSource")}</dt><dd>{emailSource==="person-profile"?t("sc.sourcePersonProfile"):emailSource==="auth-user"?t("sc.sourceAuthUser"):t("sc.sourceNone")}</dd></div>
                </dl>
              </section>
              <section className="person-card">
                <h3>{t("sc.phone")}</h3>
                <dl className="person-facts">
                  <div><dt>{t("sc.primaryPhone")}</dt><dd>{profile?.primary_phone||t("sc.notRecorded")}</dd></div>
                </dl>
              </section>
            </div>
          </div>
        }

        {tab==="addresses"&&
          <div className="person-section-body">
            <SectionHeader title={t("sc.addresses")} description={t("sc.addressesDesc")}/>
            {addresses.length?
              <ResourceTable label={t("sc.recordedAddresses")}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{t("sc.kind")}</th>
                      <th>{t("sc.label")}</th>
                      <th>{t("sc.street")}</th>
                      <th>{t("sc.postalCode")}</th>
                      <th>{t("sc.city")}</th>
                      <th>{t("sc.region")}</th>
                      <th>{t("sc.country")}</th>
                      <th>{t("sc.primary")}</th>
                      <th>{t("sc.verifiedAt")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addresses.map(a=>
                      <tr key={a.address_id}>
                        <td><StateText tone="neutral">{a.kind}</StateText></td>
                        <td>{a.label||"—"}</td>
                        <td>{a.street_line1||""}{a.street_line2?" "+a.street_line2:""}</td>
                        <td>{a.postal_code||"—"}</td>
                        <td>{a.city}</td>
                        <td>{a.region||"—"}</td>
                        <td>{a.country_code?.toUpperCase()||"—"}</td>
                        <td>{a.is_primary?t("sc.yes"):t("sc.no")}</td>
                        <td>{formatDate(a.verified_at)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </ResourceTable>
              :
              <EmptyState title={t("sc.noAddresses")} detail={t("sc.noAddressesDesc")}/>
            }
          </div>
        }

        {tab==="security"&&
          <div className="person-section-body">
            <SectionHeader title={t("sc.security")} description={t("sc.securityDesc")}/>
            <div className="person-grid">
              <section className="person-card">
                <h3>{t("sc.passkeys")} ({activePasskeys.length})</h3>
                {activePasskeys.length?
                  <ResourceList label={t("sc.passkeys")}>
                    <div className="person-list">
                      {activePasskeys.map(p=>
                        <div className="person-row" key={p.credential_uuid}>
                          <div>
                            {onOpenPasskey?
                              <button
                                type="button"
                                className="pk-link"
                                onClick={()=>onOpenPasskey(p.credential_uuid)}
                              >
                                {p.label||t("sc.unnamedPasskey")}
                              </button>
                              :<strong>{p.label||t("sc.unnamedPasskey")}</strong>}
                            <span className="person-meta">{passkeyType(p.device_type,p.backed_up)}</span>
                          </div>
                          <div className="person-row-meta">
                            <span>{p.unlock_capable?t("sc.canUnlockVault"):t("sc.signInOnly")}</span>
                            <span>{p.prf_capable?t("sc.prfCapable"):""}</span>
                            <StateText tone={statusTone(p.status)}>{statusLabel(p.status)}</StateText>
                          </div>
                        </div>
                      )}
                    </div>
                  </ResourceList>
                  :
                  <EmptyState title={t("sc.noPasskeys")} detail={t("sc.noPasskeysDesc")}/>
                }
              </section>

              <section className="person-card">
                <h3>{t("sc.sessions")} ({counts.activeSessions} {t("sc.active")})</h3>
                {sessions.length?
                  <ResourceList label={t("sc.sessions")}>
                    <div className="person-list">
                      {sessions.map(s=>{
                        const personState=personSessionState(s);
                        return <div className="person-row" key={sessionKey(s)}>
                          <div>
                            <strong>
                              {personClientDescriptor(s)
                                ||s.device_label
                                ||s.credential_label
                                ||t("sc.sessionUnknownDevice")}
                            </strong>
                            <div className="person-meta">
                              {s.client_class&&<span>{clientLabel(s.client_class)}</span>}
                              <span>{authLabel(s.auth_method)}</span>
                            </div>
                          </div>
                          <div className="person-row-meta">
                            <StateText tone={personStateTone(personState)}>{statusLabel(personState)}</StateText>
                            <span>{relativeTime(personMinutes(s.last_seen_at,sessionNow))}</span>
                            {s.is_current
                              ?<span className="current-badge">{t("sc.currentSessionBadge")}</span>
                              :null}
                          </div>
                        </div>;
                      })}
                    </div>
                  </ResourceList>
                  :
                  <EmptyState title={t("sc.noSessions")} detail={t("sc.noSessionsDesc")}/>
                }
              </section>

              <section className="person-card">
                <h3>{t("sc.recovery")}</h3>
                <dl className="person-facts">
                  <div><dt>{t("sc.unlockCredentials")}</dt><dd><strong>{recovery?.unlock_credentials??0}</strong></dd></div>
                  <div><dt>{t("sc.trustGroups")}</dt><dd><strong>{recovery?.trust_groups??0}</strong></dd></div>
                  <div><dt>{t("sc.offlineRecovery")}</dt><dd><StateText tone={recovery?.offline_recovery_ready?"positive":"attention"}>{recovery?.offline_recovery_ready?t("sc.ready"):t("sc.notReady")}</StateText></dd></div>
                  <div><dt>{t("sc.recoveryState")}</dt><dd><StateText tone={recoveryTone(recovery?.recovery_state)}>{recovery?statusLabel(recovery.recovery_state):t("sc.unknown")}</StateText></dd></div>
                </dl>
              </section>
            </div>
          </div>
        }

        {tab==="access"&&
          <div className="person-section-body">
            <SectionHeader title={t("sc.access")} description={t("sc.accessDesc")}/>
            <div className="person-grid">
              <section className="person-card">
                <h3>{t("sc.roles")}</h3>
                <ResourceList label={t("sc.roles")}>
                  <ul>
                    {roles.length?
                      roles.map(r=><li key={r}><code>{r}</code></li>)
                      :<li className="quiet-copy">{t("sc.noRoles")}</li>
                    }
                  </ul>
                </ResourceList>
              </section>
              <section className="person-card">
                <h3>{t("sc.packages")}</h3>
                <ResourceList label={t("sc.packages")}>
                  <ul>
                    {packages.length?
                      packages.map(p=><li key={p}><code>{p}</code></li>)
                      :<li className="quiet-copy">{t("sc.noPackages")}</li>
                    }
                  </ul>
                </ResourceList>
              </section>
              <section className="person-card">
                <h3>{t("sc.loginProfile")}</h3>
                <dl className="person-facts">
                  <div><dt>{t("sc.profile")}</dt><dd>{person?.login_profile_name||t("sc.none")}</dd></div>
                  <div><dt>{t("sc.audience")}</dt><dd>{person?.login_profile_audience||"—"}</dd></div>
                  <div><dt>{t("sc.selfRegistration")}</dt><dd>{person?.self_registration?t("sc.yes"):t("sc.no")}</dd></div>
                  <div><dt>{t("sc.minAssurance")}</dt><dd>{t("sc.assuranceLevel")} {person?.min_assurance??1}</dd></div>
                  <div><dt>{t("sc.sessionMaxAge")}</dt><dd>{person?.session_max_age_seconds?humanDuration(person.session_max_age_seconds):"—"}</dd></div>
                </dl>
              </section>
            </div>
          </div>
        }

        {tab==="activity"&&
          <div className="person-section-body">
            <SectionHeader title={t("sc.activity")} description={t("sc.activityDesc")}/>
            {events.length?
              <ResourceTable label={t("sc.recentEvents")}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{t("sc.time")}</th>
                      <th>{t("sc.event")}</th>
                      <th>{t("sc.resource")}</th>
                      <th>{t("sc.outcome")}</th>
                      <th>{t("sc.severity")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(e=>
                      <tr key={e.event_id}>
                        <td>{formatDate(e.occurred_at)}</td>
                        <td><strong title={e.event_type}>{eventLabel(e.event_type)}</strong></td>
                        <td title={[e.resource_type,e.resource_id].filter(Boolean).join(" ")}>{e.resource_type||"—"}{e.resource_id?" "+e.resource_id:""}</td>
                        <td>{e.outcome}</td>
                        <td><StateText tone={severityTone(e.severity)}>{statusLabel(e.severity)}</StateText></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </ResourceTable>
              :
              <EmptyState title={t("sc.noEvents")} detail={t("sc.noEventsDesc")}/>
            }
          </div>
        }
      </div>
    </>}
  </div>;
}